import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FaCalendarAlt, FaChevronLeft, FaPlay } from "react-icons/fa";
import "./roadNotes.scss";
import { useGetRoadNoteBySlugQuery } from "../features/api/youtubeApi";
import SEO from "../components/seo/SEO";
import FuelLoading from "./FuelLoading";
import VideoModal from "../components/video/VideoModal";
import Section from "../components/ui/Section";

export default function RoadNoteDetail() {
  const { slug } = useParams();

  // 1. Fetch only one specific document from MongoDB by its unique slug configuration
  const { data: note, isLoading, isError } = useGetRoadNoteBySlugQuery(slug);
  const [activeVideoIndex, setActiveVideoIndex] = useState(null);

  if (isLoading) {
    return (
      <div className="road-notes-loading-box">
        <FuelLoading text="Opening historical travel logs..." />
      </div>
    );
  }

  if (isError || !note) {
    return (
      <main className="road-note-detail-layout road-note-detail-layout--error fuel-fade-in">
        <div className="road-notes-error-view">
          <span className="error-map-icon">🗺️</span>
          <h2>Navigation Track Lost</h2>
          <p>
            This journal entry does not exist or has been routed down a
            different highway.
          </p>
          <Link to="/roadnotes" className="back-btn-link">
            ← Return to Road Notes
          </Link>
        </div>
      </main>
    );
  }

  const formattedDate = new Date(note.createdAt).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <main className="road-notes-pages">
      {/* 🟢 METADATA HOOK: Generates separate rich-snippets for Google search bots */}
      <SEO
        title={note.title}
        description={
          note.excerpt ||
          "A quiet journal log segment captured from the slow-lane perspective of a small car."
        }
        slug={`/roadnotes/${note.slug}`}
        type="article"
        datePublished={note.createdAt}
      />
      <Section>
        <div className="detail-container-chassis">
          <Link to="/roadnotes" className="global-back-nav-btn">
            <FaChevronLeft /> Back to Journal Feed
          </Link>

          <header className="article-main-header">
            <div className="article-meta-row">
              <span className="category-badge">📝 Road Notes</span>
              <time className="meta-time">
                <FaCalendarAlt /> {formattedDate}
              </time>
            </div>
            <h1>{note.title}</h1>
          </header>

          {/* Core Rich Narrative Document Workspace Box Container */}
          <article
            className="article-narrative-body text-rich"
            dangerouslySetInnerHTML={{ __html: note.content }}
          />

          {/* Attached Driving Clip Video Matrix Segments */}
          {note.videoRefs && note.videoRefs.length > 0 && (
            <section className="attached-media-section">
              <h3>Connected Drives</h3>
              <p>
                Watch driving sequences and route captures linked natively
                within this journey segment log.
              </p>

              <div className="connected-videos-grid">
                {note.videoRefs.map((v, idx) => (
                  <button
                    key={v._id}
                    type="button"
                    className="video-card-node"
                    onClick={() => setActiveVideoIndex(idx)}
                  >
                    <div className="thumb-container-frame">
                      <img
                        src={v.thumbnails?.medium?.url}
                        alt={v.title}
                        loading="lazy"
                      />
                      <div className="play-overlay-frost">
                        <FaPlay />
                      </div>
                    </div>
                    <span className="clip-title-label">{v.title}</span>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Dynamic Floating Overlay Video Canvas Portal Trigger */}
        {activeVideoIndex !== null && note.videoRefs && (
          <VideoModal
            videos={note.videoRefs}
            currentIndex={activeVideoIndex}
            onClose={() => setActiveVideoIndex(null)}
          />
        )}
      </Section>
    </main>
  );
}
